/**
 * KAN-909: умолчание фильтров по менеджеру (таблица и доска CRM, Лиды по дням,
 * Лиды по теплоте, Заказы, Клиенты).
 * Правило одно на все места:
 *   1. значение задано явно (адрес, ссылка-пресет) - оно;
 *   2. пользователь уже выбирал в этом месте (в том числе "Все менеджеры") - его выбор
 *      из localStorage, ключ по месту и пользователю;
 *   3. не выбирал и у входа есть своя запись справочника менеджера - этот менеджер;
 *   4. иначе "Все менеджеры".
 * "Не выбирал" и "выбрал всех" различаются: выбор хранится объектом { value },
 * поэтому пустая строка "Все" тоже запоминается.
 */
import { loadJson, removeJson, saveJson } from '@/lib/persistedState';

/** Место с фильтром по менеджеру: у каждого свой запомненный выбор. */
export type ManagerChoiceScope = 'crm' | 'leads' | 'orders' | 'clients';

/** Поля текущего пользователя, из которых берётся своя запись менеджера. */
export interface ManagerChoiceUser {
  id?: string;
  /** Запись справочника входа роли MANAGER (/auth/me). */
  manager_id?: string | null;
  /**
   * Запись справочника, связанная со входом любой роли (у владельца Стомбая "Стомбэй").
   * Пока backend её не присылает, работает только manager_id.
   */
  linked_manager_id?: string | null;
}

/** Значения места: что значит "Все менеджеры" и как записать "этот менеджер". */
export interface ManagerChoiceValues {
  all: string;
  mine: (managerId: string) => string;
}

/** Своя запись справочника менеджера у входа; null - записи нет. */
export function ownManagerId(user: ManagerChoiceUser | null | undefined): string | null {
  return user?.linked_manager_id || user?.manager_id || null;
}

export function managerChoiceKey(scope: ManagerChoiceScope, userId: string): string {
  return `smeta.managerFilter.v1.${scope}.${userId}`;
}

/** Запомненный выбор; null - пользователь в этом месте ещё не выбирал (или не загружен). */
export function loadManagerChoice(scope: ManagerChoiceScope, userId: string | undefined): string | null {
  if (!userId) return null;
  const raw = loadJson(managerChoiceKey(scope, userId));
  if (!raw || typeof raw !== 'object') return null;
  const value = (raw as { value?: unknown }).value;
  return typeof value === 'string' ? value : null;
}

export function saveManagerChoice(scope: ManagerChoiceScope, userId: string | undefined, value: string): void {
  if (userId) saveJson(managerChoiceKey(scope, userId), { value });
}

export function clearManagerChoice(scope: ManagerChoiceScope, userId: string | undefined): void {
  if (userId) removeJson(managerChoiceKey(scope, userId));
}

/** Умолчание места: свой менеджер, если запись есть, иначе "Все менеджеры". */
export function defaultManagerFilter(ownId: string | null, values: ManagerChoiceValues): string {
  return ownId ? values.mine(ownId) : values.all;
}

/**
 * KAN-909 (правило KAN-685): выбран менеджер, которого нет в загруженном списке
 * (удалён). Особые значения (Все, Без менеджера) и список, который ещё не
 * загружен (или не загрузился), удалённым не считаются.
 */
export function isUnknownManager(
  value: string,
  managers: readonly { id: string }[] | undefined,
  special: readonly string[]
): boolean {
  if (!managers || !value || special.includes(value)) return false;
  return !managers.some((m) => m.id === value);
}

/** Значение фильтра по правилу из шапки модуля. */
export function resolveManagerFilter(
  explicit: string | null | undefined,
  stored: string | null,
  ownId: string | null,
  values: ManagerChoiceValues
): string {
  if (explicit) return explicit;
  if (stored !== null) return stored;
  return defaultManagerFilter(ownId, values);
}
